// Phone-viewport presets for the dev-only mobile preview simulator.
// Plan: docs/planning/dev_mobile_viewport_preview.md §8.
//
// CSS pixels are deliberate; DPR / device-pixel-ratio is intentionally not
// simulated — this is a layout-validation tool, not a raster-sharpness tool.
// Tests in `tests/devViewportPresets.test.ts` lock these numbers so changing
// them is a conscious act.

export type DevViewportPresetId = "iphone-se" | "iphone-15" | "galaxy-s26-ultra";

export type DevViewportOrientation = "portrait" | "landscape";

export type DevViewportPreset = {
  id: DevViewportPresetId;
  label: string;
  /** CSS pixels in portrait orientation. Landscape swaps width <-> height. */
  portraitWidth: number;
  portraitHeight: number;
};

export const DEV_VIEWPORT_PRESETS: DevViewportPreset[] = [
  { id: "iphone-se", label: "iPhone SE (small)", portraitWidth: 375, portraitHeight: 667 },
  { id: "iphone-15", label: "iPhone 14/15 (standard)", portraitWidth: 390, portraitHeight: 844 },
  {
    id: "galaxy-s26-ultra",
    label: "Galaxy S26 Ultra",
    portraitWidth: 480,
    portraitHeight: 1040,
  },
];

const PRESET_BY_ID: Record<DevViewportPresetId, DevViewportPreset> = DEV_VIEWPORT_PRESETS.reduce(
  (acc, p) => {
    acc[p.id] = p;
    return acc;
  },
  {} as Record<DevViewportPresetId, DevViewportPreset>,
);

export function getDevViewportPreset(id: DevViewportPresetId): DevViewportPreset {
  return PRESET_BY_ID[id];
}

/**
 * Resolves a preset + orientation into the rendered CSS pixel dimensions.
 * Phase 1 ships portrait-only per docs §11; the landscape branch is wired
 * here so Phase 2 (rotate toggle) is a pure UI add.
 */
export function resolveDevViewportSize(
  id: DevViewportPresetId,
  orientation: DevViewportOrientation,
): { width: number; height: number } {
  const preset = getDevViewportPreset(id);
  if (orientation === "landscape") {
    return { width: preset.portraitHeight, height: preset.portraitWidth };
  }
  return { width: preset.portraitWidth, height: preset.portraitHeight };
}

export function isDevViewportPresetId(value: unknown): value is DevViewportPresetId {
  return value === "iphone-se" || value === "iphone-15" || value === "galaxy-s26-ultra";
}

export function isDevViewportOrientation(value: unknown): value is DevViewportOrientation {
  return value === "portrait" || value === "landscape";
}
